import React, { useState, useEffect, useRef } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FRONTEND_URL = (process.env.REACT_APP_FRONTEND_URL || (typeof window !== 'undefined' ? window.location.origin : '')).replace(/\/+$/, '');

// Fonction pour obtenir l'URL de l'image
function getImageUrl(imagePath) {
  if (!imagePath) return null;
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  const filename = imagePath.split('/').pop();
  return `${BACKEND_URL}/api/images/${filename}`;
}

// Fonction pour formater la durée
function formatDuration(sec) {
  if (!sec || isNaN(sec)) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export default function SharePlayer() {
  const [track, setTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const audioRef = useRef(new Audio());

  // Extraire l'ID de la piste depuis l'URL
  const getTrackIdFromUrl = () => {
    const path = window.location.hash || window.location.pathname;
    const match = path.match(/\/share\/track\/(\d+)/);
    return match ? match[1] : null;
  };

  // Charger les données de la piste et mettre à jour les méta tags
  useEffect(() => {
    const trackId = getTrackIdFromUrl();
    if (!trackId) {
      setError('ID de piste non trouvé');
      setLoading(false);
      return;
    }

    fetch(`${BACKEND_URL}/api/tracks/${trackId}`)
      .then(res => {
        if (!res.ok) throw new Error('Piste non trouvée');
        return res.json();
      })
      .then(data => {
        setTrack(data);
        setLoading(false);
        
        // Mettre à jour le titre de la page
        document.title = `${data.title} - ${data.artist} | Vinisys Music`;
        
        // Configurer l'audio
        let audioUrl;
        if (data.file_path.startsWith('http://') || data.file_path.startsWith('https://')) {
          audioUrl = data.file_path;
        } else {
          const filename = data.file_path.split('/').pop();
          audioUrl = `${BACKEND_URL}/api/audio/${filename}`;
        }
        audioRef.current.src = audioUrl;
      })
      .catch(err => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  // Gérer les événements audio
  useEffect(() => {
    const audio = audioRef.current;

    const handleTimeUpdate = () => {
      if (audio.duration) {
        setProgress((audio.currentTime / audio.duration) * 100);
        setCurrentTime(audio.currentTime);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setProgress(0);
      setCurrentTime(0);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.pause();
    };
  }, []);

  const togglePlay = () => {
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = ((e.clientX - rect.left) / rect.width) * 100;
    const newTime = (percent / 100) * audioRef.current.duration;
    audioRef.current.currentTime = newTime;
    setProgress(percent);
  };

  const goToApp = () => {
    const base = FRONTEND_URL || (typeof window !== 'undefined' ? window.location.origin.replace(/\/+$/, '') : '');
    const trackId = getTrackIdFromUrl();
    if (trackId) {
      window.location.href = `${base}/#/?playTrack=${trackId}`;
    } else {
      window.location.href = `${base}/#/`;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-black flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-white/20 border-t-green-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  if (error || !track) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-zinc-900 to-black flex flex-col items-center justify-center text-white p-4">
        <div className="text-6xl mb-4">🎵</div>
        <h1 className="text-2xl font-bold mb-2">Piste non trouvée</h1>
        <p className="text-white/60 mb-6">{error || 'Cette piste n\'existe pas ou a été supprimée.'}</p>
        <button
          onClick={goToApp}
          className="px-6 py-3 bg-green-500 hover:bg-green-400 rounded-full text-black font-semibold transition-colors"
        >
          Découvrir Vinisys Music
        </button>
      </div>
    );
  }

  const coverUrl = getImageUrl(track.cover_image);

  return (
    <div className="min-h-screen bg-gradient-to-b from-zinc-900 via-zinc-800 to-black flex flex-col items-center justify-center p-4">
      {/* Card principale */}
      <div className="w-full max-w-md bg-zinc-900/80 backdrop-blur-xl rounded-2xl overflow-hidden shadow-2xl border border-white/10">
        {/* Cover Image */}
        <div className="aspect-square w-full overflow-hidden">
          {coverUrl ? (
            <img 
              src={coverUrl} 
              alt={track.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center text-8xl">
              {track.cover_emoji || '🎵'}
            </div>
          )}
        </div>

        {/* Track Info */}
        <div className="p-6">
          <h1 className="text-white text-2xl font-bold truncate">{track.title}</h1>
          <p className="text-white/60 text-lg truncate">{track.artist}</p>

          {/* Progress Bar */}
          <div className="mt-6">
            <div 
              className="w-full h-1.5 bg-white/20 rounded-full cursor-pointer"
              onClick={handleSeek}
            >
              <div 
                className="h-full bg-green-500 rounded-full relative transition-all duration-150"
                style={{ width: `${progress}%` }}
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full shadow-lg" />
              </div>
            </div>
            <div className="flex justify-between mt-2 text-sm text-white/60">
              <span>{formatDuration(currentTime)}</span>
              <span>{formatDuration(track.duration)}</span>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center justify-center gap-6 mt-6">
            <button
              onClick={togglePlay}
              className="w-16 h-16 rounded-full bg-green-500 hover:bg-green-400 hover:scale-105 flex items-center justify-center text-black transition-all shadow-lg"
            >
              {isPlaying ? (
                <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M6 4h4v16H6V4zm8 0h4v16h-4V4z"/>
                </svg>
              ) : (
                <svg className="w-7 h-7 ml-1" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8 5v14l11-7z"/>
                </svg>
              )}
            </button>
          </div>

          {/* Open in App Button */}
          <button
            onClick={goToApp}
            className="w-full mt-6 py-3 bg-white/10 hover:bg-white/20 rounded-full text-white font-medium transition-colors flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
            </svg>
            Ouvrir dans Vinisys Music
          </button>
        </div>
      </div>

      {/* Footer branding */}
      <div className="mt-8 text-white/40 text-sm flex items-center gap-2">
        <span>Propulsé par</span>
        <span className="font-semibold text-white/60">Vinisys Music</span>
      </div>
    </div>
  );
}
